// @flow

import InFilter from '../../../common/support/utils/druid/filters/InFilter';

import { ALL_NAMED_EVENT_NAMES } from '../../../common/support/eo_mapping/event_names';

import { DEFAULT_DRUID_FILTERS_LIST } from '../../../common/components/core/PureQueryBuilder/utils';

import { JOURNEYS_ANALYTICS_FILTERS_LIST } from '../ScreenJourneys/utils/journeyConstants';

import {
  SOURCE_QUERIES,
  ADS_TIMESERIES_SOURCE_QUERIES,
  EMAIL_SOURCE_QUERIES,
  QUICK_LINKS_SOURCE_QUERIES
} from '../../../common/support/eo_mapping/source_queries';

import { convertMultipleQueriesToNativeDruidFilters } from '../../../common/support/utils/druid/nativeQueries';

import type {
  ProductFilterMapping,
  ProductValueLabelMapping,
  ProductSourceQueryMapping,
  SummaryTileConfig
} from './newSummaryTypes';

// ---------------------- MISCELLANEOUS ---------------------- //
export const INIT_DEFAULT_RANGE = 7; // default date range will be last 7 days
export const SUMMARY_PAGE_STYLE_VARIANT = 'summary-page';
export const NUM_PRODUCTS = 5; // 5 total products - all, quick links, journeys, universal ads, universal email
export const SUMMARY_PAGE_DOCS_LINK =
  'https://help.branch.io/using-branch/docs/branch-summary-page';

// ---------------------- DEFAULT DIMENSIONS ---------------------- //
export const DEFAULT_TIMESERIES_DIMENSIONS = [
  'last_attributed_touch_data_tilde_advertising_partner_name'
];
export const DEFAULT_TABLE_DIMENSIONS = [
  'last_attributed_touch_data_tilde_advertising_partner_name'
];
export const DEFAULT_TABLE_COLUMNS = [
  'eo_click',
  'eo_install',
  'eo_open',
  'eo_reinstall'
];

// ---------------------- COMPARE BY AND FILTER DIMENSIONS ---------------------- //
export const TILE_FILTER_DIMENSIONS = [
  ...DEFAULT_DRUID_FILTERS_LIST,
  ...JOURNEYS_ANALYTICS_FILTERS_LIST
];
export const TIMESERIES_COMPAREBY_DIMENSIONS = [
  ...DEFAULT_DRUID_FILTERS_LIST,
  ...JOURNEYS_ANALYTICS_FILTERS_LIST
];
export const TABLE_COMPAREBY_DIMENSIONS = [
  ...DEFAULT_DRUID_FILTERS_LIST,
  ...JOURNEYS_ANALYTICS_FILTERS_LIST
];

// ---------------------- PRODUCT LABELS ---------------------- //
export const ALL_DATA_LABEL = 'All Data';
export const JOURNEYS_LABEL = 'Journeys';
export const QUICK_LINKS_LABEL = 'Quick Links';
export const UNIVERSAL_ADS_LABEL = 'Universal Ads';
export const UNIVERSAL_EMAIL_LABEL = 'Universal Email';

// ---------------------- PRODUCT VALUES ---------------------- //
export const ALL_DATA_VALUE = 'all';
export const JOURNEYS_VALUE = 'journeys';
export const QUICK_LINKS_VALUE = 'quick-links';
export const UNIVERSAL_ADS_VALUE = 'universal-ads';
export const UNIVERSAL_EMAIL_VALUE = 'universal-email';

// ---------------------- REQUIRED FILTER DIMENSIONS ---------------------- //
export const JOURNEYS_FILTER_DIMENSION =
  'last_attributed_touch_data_tilde_feature';
export const QUICK_LINKS_FILTER_DIMENSION =
  'last_attributed_touch_data_tilde_id';
export const ADS_FILTER_DIMENSION = 'last_attributed_touch_data_tilde_feature';
// no required filter dimension for EMAIL

// --------------------------- REQUIRED FILTERS --------------------------- //
const JOURNEYS_REQUIRED_FILTER = new InFilter(JOURNEYS_FILTER_DIMENSION, [
  'journeys'
]);
const ADS_REQUIRED_FILTER = new InFilter(ADS_FILTER_DIMENSION, [
  'paid advertising'
]);
const QUICK_LINKS_REQUIRED_FILTER = {};
const EMAIL_REQUIRED_FILTER = {};

export const REQUIRED_FILTERS = [
  JOURNEYS_REQUIRED_FILTER,
  QUICK_LINKS_REQUIRED_FILTER,
  ADS_REQUIRED_FILTER,
  EMAIL_REQUIRED_FILTER
];

// ---------------------------- SOURCE QUERIES ---------------------------- //
export const ALL_DATA_SOURCE_QUERY = convertMultipleQueriesToNativeDruidFilters(
  SOURCE_QUERIES
);
export const JOURNEYS_SOURCE_QUERY = convertMultipleQueriesToNativeDruidFilters(
  SOURCE_QUERIES
);
export const QUICK_LINKS_SOURCE_QUERY = convertMultipleQueriesToNativeDruidFilters(
  QUICK_LINKS_SOURCE_QUERIES
);
export const ADS_SOURCE_QUERY = convertMultipleQueriesToNativeDruidFilters(
  SOURCE_QUERIES
);
export const ADS_TIMESERIES_SOURCE_QUERY = convertMultipleQueriesToNativeDruidFilters(
  ADS_TIMESERIES_SOURCE_QUERIES
); // special source query for ads timeseries
export const EMAIL_SOURCE_QUERY = convertMultipleQueriesToNativeDruidFilters(
  EMAIL_SOURCE_QUERIES
);

// ---------------------------- PRODUCT MAPPINGS ---------------------------- //
export const productValueLabelMapping: ProductValueLabelMapping = {
  [ALL_DATA_VALUE]: ALL_DATA_LABEL,
  [JOURNEYS_VALUE]: JOURNEYS_LABEL,
  [QUICK_LINKS_VALUE]: QUICK_LINKS_LABEL,
  [UNIVERSAL_ADS_VALUE]: UNIVERSAL_ADS_LABEL,
  [UNIVERSAL_EMAIL_VALUE]: UNIVERSAL_EMAIL_LABEL
};

export const productFilterMapping: ProductFilterMapping = {
  [ALL_DATA_VALUE]: {},
  [JOURNEYS_VALUE]: JOURNEYS_REQUIRED_FILTER,
  [QUICK_LINKS_VALUE]: QUICK_LINKS_REQUIRED_FILTER,
  [UNIVERSAL_ADS_VALUE]: ADS_REQUIRED_FILTER,
  [UNIVERSAL_EMAIL_VALUE]: EMAIL_REQUIRED_FILTER
};

export const productSourceQueryMapping: ProductSourceQueryMapping = {
  [ALL_DATA_VALUE]: ALL_DATA_SOURCE_QUERY,
  [JOURNEYS_VALUE]: JOURNEYS_SOURCE_QUERY,
  [QUICK_LINKS_VALUE]: QUICK_LINKS_SOURCE_QUERY,
  [UNIVERSAL_ADS_VALUE]: ADS_SOURCE_QUERY,
  [UNIVERSAL_EMAIL_VALUE]: EMAIL_SOURCE_QUERY
};

// ---------------------------- INIT TILE CONFIG ---------------------------- //
export const INIT_TILE_CONFIG: SummaryTileConfig = {
  'summary-tile-0': {
    dataSource: 'eo_click',
    filter: {},
    organic: false,
    unique: true
  },
  'summary-tile-1': {
    dataSource: 'eo_install',
    filter: {},
    organic: false,
    unique: true
  },
  'summary-tile-2': {
    dataSource: 'eo_open',
    filter: {},
    organic: false,
    unique: true
  },
  'summary-tile-3': {
    dataSource: 'eo_reinstall',
    filter: {},
    organic: false,
    unique: true
  }
};

// ---------------------------- TILE EVENT LIST ---------------------------- //
export const TILE_EVENT_LIST = Object.keys(ALL_NAMED_EVENT_NAMES);
